import React, { Fragment } from 'react';
import styled from 'styled-components/macro';
import { TCustomListInvitationRoles } from '../AddToList/shared';
import { hideVisually } from 'polished';
import P from 'components/elements/p';

const Fieldset = styled('div')`
  padding: ${({ theme }): string => `${theme.spacing.small} 0`};
  margin-bottom: ${({ theme }): string => `${theme.spacing.xlarge}`};
  border-bottom: ${({ theme }): string => `1px solid ${theme.CasperGrey}`};
`;

const RadioWrapper = styled.div`
  padding: ${({ theme }): string =>
    `0 0 ${theme.spacing.small} calc(${radioIconDimension}px * 1.5)`};
`;

const Label = styled('label')``;

const radioIconDimension = 16;
const radioIconDotDimension = 4;

const Radio = styled('input')`
  ${hideVisually};

  + label {
    position: relative;
    display: inline-block;
    cursor: pointer;

    &::before,
    &::after {
      left: 0;
      transform: translateY(3px)
        translateX(calc(-${radioIconDimension}px * 1.5));
      border-radius: 50%;
      position: absolute;
      display: inline-block;
      content: '';
    }

    &::before {
      border: ${({ theme }): string => `1px solid ${theme.CasperGrey}`};
      width: ${radioIconDimension}px;
      height: ${radioIconDimension}px;
      transition: ${({ theme }): string =>
        `${theme.transitionDuration} ${theme.timingFunction} background`};
    }

    &::after {
      left: ${radioIconDimension / 2 - radioIconDotDimension / 2}px;
      top: ${radioIconDimension / 2 - radioIconDotDimension / 2}px;
      width: ${radioIconDotDimension}px;
      height: ${radioIconDotDimension}px;
    }
  }

  &:checked {
    + label::before {
      border-color: transparent;
      background: ${({ theme }): string => theme.AccentGreen};
    }
    + label::after {
      background: ${({ theme }): string => theme.White};
    }
  }

  &:disabled + label {
    color: ${({ theme }): string => theme.CasperGrey};
    cursor: not-allowed;
  }
`;

interface IRadioItem {
  item: IRadioGroupOptionsItem;
  selectedOption: string;
  onChange?: ({
    label,
    value,
  }: {
    label: string;
    value: TCustomListInvitationRoles;
  }) => any;
  name: string;
  id: string;
}

const RadioItem: React.FC<IRadioItem> = (props) => {
  const { item, selectedOption, onChange, name, id } = props;
  const { label, value, disabled, description } = item;

  return (
    <Fragment key={value}>
      <RadioWrapper>
        <Radio
          type="radio"
          name={name}
          value={value}
          id={id}
          checked={selectedOption === value}
          onChange={(): any => {
            if (typeof onChange === 'function') {
              onChange({ label, value });
            }
          }}
          disabled={disabled}
        />
        <Label htmlFor={id}>{label}</Label>
        <P color={'CasperGrey'} fontSize="1">
          {description}
        </P>
      </RadioWrapper>
    </Fragment>
  );
};

export interface IRadioGroupOptionsItem {
  label: string;
  value: TCustomListInvitationRoles;
  disabled: boolean;
  description: string;
}

interface IRadioGroup {
  name: string;
  options: IRadioGroupOptionsItem[];
  selectedOption: TCustomListInvitationRoles;
  onChange?: ({
    label,
    value,
  }: {
    label: string;
    value: TCustomListInvitationRoles;
  }) => any;
}

const RadioGroup: React.FC<IRadioGroup> = (props) => {
  const { options, ...rest } = props;

  return (
    <Fieldset>
      {options.map((item) => {
        const id = `radio-group-${item.value}`;
        return <RadioItem key={id} item={item} id={id} {...rest} />;
      })}
    </Fieldset>
  );
};

export default RadioGroup;
